import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { createLogger } from "@/lib/logger";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  CheckCircle,
  XCircle,
  Clock,
  Pause,
  Ban,
  ChevronDown,
  ChevronUp,
  Play,
  MessageSquare,
  AlertCircle,
} from "lucide-react";
import type { Tables } from "@/integrations/supabase/types";

const logger = createLogger("FlowExecutionHistory");

/**
 * Mapeamento de status para ícones e cores
 */
const statusConfig: Record<string, {
  icon: typeof CheckCircle;
  label: string;
  color: string;
  variant: "default" | "destructive" | "secondary" | "outline";
}> = {
  completed: {
    icon: CheckCircle,
    label: "Concluído",
    color: "text-green-500",
    variant: "default" as const,
  },
  failed: {
    icon: XCircle,
    label: "Falhou",
    color: "text-red-500",
    variant: "destructive" as const,
  },
  running: {
    icon: Play,
    label: "Executando",
    color: "text-blue-500",
    variant: "secondary" as const,
  },
  paused: {
    icon: Pause,
    label: "Pausado",
    color: "text-yellow-500",
    variant: "secondary" as const,
  },
  cancelled: {
    icon: Ban,
    label: "Cancelado",
    color: "text-gray-500",
    variant: "outline" as const,
  },
  waiting_input: {
    icon: MessageSquare,
    label: "Aguardando Resposta",
    color: "text-purple-500",
    variant: "secondary" as const,
  },
  waiting_delay: {
    icon: Clock,
    label: "Aguardando Delay",
    color: "text-amber-500",
    variant: "secondary" as const,
  },
};

/**
 * Mapeamento de trigger types para labels
 */
const triggerTypeLabels = {
  keyword: "Palavra-chave",
  first_message: "Primeira mensagem",
  scheduled: "Agendado",
  inactivity: "Inatividade",
  event: "Evento",
  webhook: "Webhook",
};

interface FlowExecutionHistoryProps {
  flowId: string;
}

/**
 * Componente que exibe o histórico completo de execuções de um fluxo
 * com detalhes de cada passo executado
 */
export function FlowExecutionHistory({ flowId }: FlowExecutionHistoryProps) {
  const [expandedExecutions, setExpandedExecutions] = useState<Set<string>>(
    new Set()
  );

  // Buscar execuções do fluxo
  const { data: executions = [], isLoading } = useQuery({
    queryKey: ["flow-executions", flowId],
    queryFn: async () => {
      logger.info("Buscando execuções do fluxo", { flowId });

      const { data, error } = await supabase
        .from("flow_executions")
        .select("*")
        .eq("flow_id", flowId)
        .order("started_at", { ascending: false })
        .limit(50);

      if (error) {
        logger.error("Erro ao buscar execuções", { error });
        throw error;
      }

      logger.debug("Execuções carregadas", { count: data?.length || 0 });
      return data || [];
    },
  });

  const toggleExecution = (executionId: string) => {
    setExpandedExecutions((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(executionId)) {
        newSet.delete(executionId);
      } else {
        newSet.add(executionId);
      }
      return newSet;
    });
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Histórico de Execuções</CardTitle>
          <CardDescription>Carregando execuções...</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-20 w-full" />
          ))}
        </CardContent>
      </Card>
    );
  }

  if (executions.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Histórico de Execuções</CardTitle>
          <CardDescription>
            Este fluxo ainda não foi executado
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <Clock className="h-16 w-16 text-muted-foreground mb-4" />
          <p className="text-muted-foreground text-center">
            As execuções aparecerão aqui quando o fluxo for ativado
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Histórico de Execuções</CardTitle>
        <CardDescription>
          Últimas {executions.length} execuções do fluxo
        </CardDescription>
      </CardHeader>
      <CardContent>
        <ScrollArea className="h-[600px] pr-4">
          <div className="space-y-3">
            {executions.map((execution) => (
              <ExecutionItem
                key={execution.id}
                execution={execution}
                isExpanded={expandedExecutions.has(execution.id)}
                onToggle={() => toggleExecution(execution.id)}
              />
            ))}
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
}

/**
 * Card individual de uma execução
 */
interface ExecutionItemProps {
  execution: Tables<"flow_executions">;
  isExpanded: boolean;
  onToggle: () => void;
}

function ExecutionItem({
  execution,
  isExpanded,
  onToggle,
}: ExecutionItemProps) {
  // Fallback para status desconhecido
  const config = statusConfig[execution.status] || {
    icon: AlertCircle,
    label: execution.status,
    color: "text-gray-500",
    variant: "outline" as const,
  };
  const Icon = config.icon;

  // Calcular duração
  const duration =
    execution.completed_at && execution.started_at
      ? Math.round(
          (new Date(execution.completed_at).getTime() -
            new Date(execution.started_at).getTime()) /
            1000
        )
      : null;

  return (
    <Collapsible open={isExpanded} onOpenChange={onToggle}>
      <Card className={`border-l-4 ${config.color.replace('text-', 'border-')}`}>
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer hover:bg-accent/50 transition-colors">
            <div className="flex items-start justify-between">
              <div className="space-y-2 flex-1">
                <div className="flex items-center gap-3">
                  <Icon className={`h-5 w-5 ${config.color}`} />
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant={config.variant}>{config.label}</Badge>
                    <Badge variant="outline">
                      {triggerTypeLabels[execution.trigger_type]}
                    </Badge>
                    {duration !== null && (
                      <span className="text-xs text-muted-foreground">
                        {duration}s
                      </span>
                    )}
                  </div>
                </div>

                <div className="text-sm text-muted-foreground">
                  Iniciado{" "}
                  {formatDistanceToNow(new Date(execution.started_at), {
                    addSuffix: true,
                    locale: ptBR,
                  })}
                  {execution.completed_at && (
                    <>
                      {" "}
                      • Concluído{" "}
                      {formatDistanceToNow(new Date(execution.completed_at), {
                        addSuffix: true,
                        locale: ptBR,
                      })}
                    </>
                  )}
                </div>

                {execution.error_message && (
                  <div className="flex items-start gap-2 text-sm text-destructive bg-destructive/10 p-2 rounded">
                    <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <span className="flex-1">{execution.error_message}</span>
                  </div>
                )}
              </div>

              <Button variant="ghost" size="sm">
                {isExpanded ? (
                  <ChevronUp className="h-4 w-4" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )}
              </Button>
            </div>
          </CardHeader>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <CardContent className="pt-0">
            <ExecutionSteps executionId={execution.id} />
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
}

/**
 * Lista de passos de uma execução
 */
interface ExecutionStepsProps {
  executionId: string;
}

function ExecutionSteps({ executionId }: ExecutionStepsProps) {
  const { data: steps = [], isLoading } = useQuery({
    queryKey: ["flow-execution-steps", executionId],
    queryFn: async () => {
      logger.info("Buscando steps da execução", { executionId });

      const { data, error } = await supabase
        .from("flow_execution_steps")
        .select("*")
        .eq("execution_id", executionId)
        .order("started_at", { ascending: true });

      if (error) {
        logger.error("Erro ao buscar steps", { error });
        throw error;
      }

      logger.debug("Steps carregados", { count: data?.length || 0 });
      return data || [];
    },
  });

  if (isLoading) {
    return (
      <div className="space-y-2 mt-4">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
      </div>
    );
  }

  if (steps.length === 0) {
    return (
      <div className="text-sm text-muted-foreground text-center py-4">
        Nenhum passo registrado
      </div>
    );
  }

  return (
    <div className="space-y-3 mt-4">
      <h4 className="font-semibold text-sm">Passos Executados:</h4>
      <div className="space-y-2">
        {steps.map((step, index) => (
          <StepItem key={step.id} step={step} index={index} />
        ))}
      </div>
    </div>
  );
}

/**
 * Item individual de um passo
 */
interface StepItemProps {
  step: Tables<"flow_execution_steps">;
  index: number;
}

function StepItem({ step, index }: StepItemProps) {
  const stepStatusConfig: Record<string, { icon: typeof CheckCircle; color: string }> = {
    completed: { icon: CheckCircle, color: "text-green-500" },
    failed: { icon: XCircle, color: "text-red-500" },
    running: { icon: Play, color: "text-blue-500" },
    pending: { icon: Clock, color: "text-gray-400" },
    skipped: { icon: Ban, color: "text-gray-400" },
    waiting: { icon: Clock, color: "text-amber-500" },
  };

  // Fallback para status desconhecido
  const config = stepStatusConfig[step.status] || { icon: AlertCircle, color: "text-gray-500" };
  const Icon = config.icon;

  const duration =
    step.completed_at && step.started_at
      ? Math.round(
          (new Date(step.completed_at).getTime() -
            new Date(step.started_at).getTime()) /
            1000
        )
      : null;

  return (
    <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/50 border">
      <div className="flex-shrink-0 flex items-center justify-center w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-semibold">
        {index + 1}
      </div>
      
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <Icon className={`h-4 w-4 ${config.color}`} />
          <span className="font-medium text-sm">{step.node_type}</span>
          {duration !== null && (
            <span className="text-xs text-muted-foreground">({duration}s)</span>
          )}
        </div>

        <div className="text-xs text-muted-foreground mb-2">
          ID do Nó: {step.node_id}
        </div>

        {step.error_message && (
          <div className="text-xs text-destructive bg-destructive/10 p-2 rounded mt-2">
            ⚠️ {step.error_message}
          </div>
        )}

        {step.output_data && (
          <details className="mt-2">
            <summary className="text-xs cursor-pointer text-primary hover:underline">
              Ver dados de saída
            </summary>
            <pre className="text-xs bg-background p-2 rounded mt-1 overflow-x-auto">
              {JSON.stringify(step.output_data, null, 2)}
            </pre>
          </details>
        )}
      </div>
    </div>
  );
}
